import { Check, Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { ColorSwatch } from "@/components/ui/color-swatch";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { TagChip } from "@/components/ui/tag-chip";
import { useCategories, type DatabaseCategory } from "@/hooks/useCategories";
import { pickColorForNewTag } from "@/lib/colorUtils";
import { offlineMutate } from "@/lib/offlineToast";

type TagInputProps = {
  value: string[]; // selected category IDs
  onChange: (ids: string[]) => void;
  placeholder?: string;
  creatable?: boolean;
};

export const TagInput = ({
  value,
  onChange,
  placeholder = "Select or type to add tags…",
  creatable = true,
}: TagInputProps) => {
  const { categories, createCategory } = useCategories();
  const [localCategories, setLocalCategories] = useState<DatabaseCategory[]>(
    [],
  );
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const mergedCategories = useMemo(() => {
    const byId = new Map<string, DatabaseCategory>();
    categories.forEach((c) => byId.set(c.id, c));
    localCategories.forEach((c) => {
      if (!byId.has(c.id)) byId.set(c.id, c);
    });
    return Array.from(byId.values());
  }, [categories, localCategories]);

  const selected = useMemo(
    () =>
      value
        .map((id) => mergedCategories.find((c) => c.id === id))
        .filter(Boolean) as DatabaseCategory[],
    [value, mergedCategories],
  );

  const options = useMemo(() => {
    if (!query.trim()) {
      return mergedCategories;
    }
    return mergedCategories.filter((c) =>
      c.name.toLowerCase().includes(query.toLowerCase()),
    );
  }, [mergedCategories, query]);

  const toggleId = (id: string) => {
    if (value.includes(id)) {
      onChange(value.filter((v) => v !== id));
    } else {
      onChange([...value, id]);
    }
    // Clear the search query after selecting/deselecting
    setQuery("");
  };

  const handleCreate = async (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      // avoid duplicates by name (case-insensitive)
      const existing = categories.find(
        (c) => c.name.toLowerCase() === trimmed.toLowerCase(),
      );
      if (existing) {
        if (!value.includes(existing.id)) onChange([...value, existing.id]);
        setQuery("");
        return;
      }
      // choose a color that's highly differentiated from existing tags
      const existingColors = mergedCategories.map((c) => c.color);
      const color = pickColorForNewTag(existingColors);
      // create category with a differentiated color
      const newCat = await offlineMutate(createCategory, {
        name: trimmed,
        color,
      });
      // Optimistically include created category so the badge appears immediately
      const catId = (newCat as DatabaseCategory)?.id ?? crypto.randomUUID();
      if (newCat) {
        setLocalCategories((prev) => [...prev, newCat as DatabaseCategory]);
      }
      onChange([...value, catId]);
      setQuery("");
    } catch (error) {
      console.error("Failed to create category:", error);
    }
  };

  return (
    <div className="w-full">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div
            className="glass-surface flex min-h-10 cursor-text flex-wrap gap-2 rounded-md border px-2 py-2"
            onClick={() => setOpen(true)}
          >
            {selected.length === 0 && (
              <span className="text-sm text-muted-foreground">
                {placeholder}
              </span>
            )}
            {selected.map((cat) => (
              <TagChip
                key={cat.id}
                label={cat.name}
                color={cat.color}
                className="pr-1"
              >
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="ml-1 h-5 w-5 p-0 text-xs opacity-80 hover:opacity-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleId(cat.id);
                  }}
                  aria-label={`Remove ${cat.name}`}
                >
                  ×
                </Button>
              </TagChip>
            ))}
          </div>
        </PopoverTrigger>
        <PopoverContent className="w-[320px] p-0">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search or create…"
              value={query}
              onValueChange={setQuery}
              onKeyDown={(e) => {
                if (e.key === "Enter" && query.trim() && creatable) {
                  e.preventDefault();
                  const exactMatch = options.find(
                    (c) => c.name.toLowerCase() === query.trim().toLowerCase(),
                  );
                  if (exactMatch) {
                    toggleId(exactMatch.id);
                  } else {
                    void handleCreate(query);
                  }
                }
                if (e.key === "Escape") {
                  setOpen(false);
                }
              }}
            />
            <CommandList>
              <CommandEmpty>
                {creatable ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start"
                    onClick={() => void handleCreate(query)}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Create “{query.trim()}”
                  </Button>
                ) : (
                  "No results"
                )}
              </CommandEmpty>
              <CommandGroup heading="Categories">
                {options.map((cat) => {
                  const checked = value.includes(cat.id);
                  return (
                    <CommandItem
                      key={cat.id}
                      onSelect={() => toggleId(cat.id)}
                      className="flex items-center gap-2"
                    >
                      <ColorSwatch color={cat.color} />
                      <span className="flex-1">{cat.name}</span>
                      {checked && <Check className="h-4 w-4" />}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};
